/**
 * SnapboothTopbar.tsx
 * Barre supérieure contextuelle Snapbooth Pro
 * Design Dark Mode avec logo, outils réactifs et bouton export
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../../services/StateService';
import type { ShapeData } from '../../../types/ShapeTypes';
import { useTheme } from '../../../hooks/useTheme';
import { SaveIndicator } from './SaveIndicator';
import { Tooltip as EnhancedTooltip } from '../common/Tooltip';
import { ExportModal } from '../ExportModal';
import { SaveConflictModal } from '../common/SaveConflictModal';
import { alignmentService } from '../../../services/AlignmentService';
import { useToast } from '../common/ToastProvider';
import { useAppCoordinatorOptional } from '../../../hooks/useAppCoordinator';
import { logger } from '../../../utils/ProfessionalLogger';
import { IconButton, Button } from '../../../ui';
import {
  LayoutGrid,
  Save,
  HelpCircle,
  Bell,
  Sun,
  Moon,
  Download,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignStartVertical as AlignTop,
  AlignCenterVertical as AlignVerticalCenter,
  AlignEndVertical as AlignBottom,
  AlignHorizontalDistributeCenter as DistributeHorizontal,
  AlignVerticalDistributeCenter as DistributeVertical,
  Move,
  Copy,
  Trash2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Undo2,
  Redo2,
  Grid3x3,
  Square,
  Bold,
  Italic,
  Underline,
  AlignJustify,
  Minus,
  Plus,
} from 'lucide-react';

/**
 * Composant SnapboothTopbar
 */
export function SnapboothTopbar(): React.ReactElement {
  const navigate = useNavigate();
  // P0.2 FIX: Utiliser selection.selectedIds et selection.primaryId au lieu de canvas.selectedNodeId/selectedNodeIds
  const selectedNodeIds = useAppStore(state => state.selection.selectedIds);
  const selectedNodeId = useAppStore(state => state.selection.primaryId);
  const shapes = useAppStore(state => state.shapes);
  const isExporting = useAppStore(state => state.canvas.isExporting);
  const saveStatus = useAppStore(state => state.save.status);
  const appCoordinator = useAppCoordinatorOptional();
  const { showSuccess, showError } = useToast();

  // AMÉLIORATION: Détecter correctement la multi-sélection
  const isMultiSelection = selectedNodeIds.length > 1;
  const selectedShapes = isMultiSelection
    ? shapes.filter(s => selectedNodeIds.includes(s.id))
    : selectedNodeId
      ? shapes.filter(s => s.id === selectedNodeId)
      : [];

  const [showExportModal, setShowExportModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingProjectName, setEditingProjectName] = useState(false);

  // Nom du projet (documentName) éditable depuis la topbar.
  const documentName = useAppStore(state => state.canvas.documentName);
  const setDocumentName = useAppStore(state => state.setDocumentName);
  const [nameValue, setNameValue] = useState(documentName);
  // Sync si le projet change (rechargement depuis Supabase).
  useEffect(() => {
    setNameValue(documentName);
  }, [documentName]);

  const commitProjectName = () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== documentName) {
      setDocumentName(trimmed);
    } else {
      setNameValue(documentName);
    }
    setEditingProjectName(false);
  };

  // Ouverture de la modale d'export depuis la Command Palette (⌘K → Exporter).
  useEffect(() => {
    const openExport = () => setShowExportModal(true);
    window.addEventListener('snapbooth:open-export', openExport);
    return () => window.removeEventListener('snapbooth:open-export', openExport);
  }, []);

  // Fonction de sauvegarde manuelle
  const handleManualSave = useCallback(async () => {
    if (!appCoordinator) {
      showError('AppCoordinator non disponible');
      return;
    }

    if (isSaving || saveStatus === 'saving') {
      return; // Éviter les sauvegardes multiples simultanées
    }

    setIsSaving(true);
    try {
      await appCoordinator.saveProject(false); // false = ne pas télécharger de fichier
      showSuccess('Projet sauvegardé avec succès !');
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde manuelle:', error);
      showError('Erreur lors de la sauvegarde du projet');
    } finally {
      setIsSaving(false);
    }
  }, [appCoordinator, isSaving, saveStatus, showSuccess, showError]);

  // Raccourci clavier Ctrl+S pour sauvegarder
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault(); // Empêcher le comportement par défaut du navigateur
        handleManualSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleManualSave]);

  return (
    <header className="snapbooth-topbar" role="banner">
      {/* Gauche : Logo + nom du projet éditable (double-clic ou F2). */}
      <div className="topbar-left">
        <div className="topbar-logo">
          <div className="logo-icon">SP</div>
          <div className="logo-text">
            {editingProjectName ? (
              <input
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onBlur={commitProjectName}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitProjectName();
                  }
                  if (e.key === 'Escape') {
                    setNameValue(documentName);
                    setEditingProjectName(false);
                  }
                }}
                autoFocus
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--color-primary, #7C3AED)',
                  outline: 'none',
                  color: 'var(--color-text-primary)',
                  fontSize: 'inherit',
                  fontWeight: 600,
                  width: Math.max(nameValue.length * 8, 100),
                  padding: 0,
                }}
              />
            ) : (
              <div
                className="logo-title"
                onDoubleClick={() => {
                  setNameValue(documentName);
                  setEditingProjectName(true);
                }}
                title="Double-cliquer pour renommer le projet"
                style={{ cursor: 'text' }}
              >
                {documentName || 'Document sans titre'}
              </div>
            )}
            <div className="logo-badge">Advanced features</div>
          </div>
        </div>
      </div>

      {/* Centre : Outils contextuels réactifs */}
      <div className="topbar-center">
        {isMultiSelection ? (
          <MultiSelectionTools shapes={selectedShapes} />
        ) : selectedShapes.length === 1 ? (
          <SingleSelectionTools shape={selectedShapes[0]} />
        ) : (
          <OverviewTools />
        )}
      </div>

      {/* Droite : Actions */}
      <div className="topbar-right flex items-center gap-2">
        <EnhancedTooltip content="Ouvrir le tableau de bord" shortcut="Ctrl+D">
          <IconButton
            variant="ghost"
            size="md"
            onClick={() => navigate('/dashboard')}
            aria-label="Ouvrir le tableau de bord"
            icon={<LayoutGrid className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        {/* AMÉLIORATION: Bouton de sauvegarde manuelle */}
        <EnhancedTooltip content="Sauvegarder le projet" shortcut="Ctrl+S">
          <Button
            variant="ghost"
            size="md"
            onClick={handleManualSave}
            disabled={isSaving || saveStatus === 'saving' || !appCoordinator}
            leftIcon={<Save className="w-4 h-4" />}
            isLoading={isSaving || saveStatus === 'saving'}
            aria-label="Sauvegarder le projet"
          >
            {isSaving || saveStatus === 'saving' ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </EnhancedTooltip>
        {/* AMÉLIORATION: Indicateur de sauvegarde */}
        <SaveIndicator />
        {/* Couche 5: Indicateur d'export */}
        {isExporting && (
          <div className="flex items-center gap-2 px-3 text-sm text-gray-600">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            <span>Export en cours...</span>
          </div>
        )}
        <EnhancedTooltip content="Ouvrir le guide utilisateur" shortcut="F1">
          <IconButton
            variant="ghost"
            size="md"
            onClick={() => {
              const event = new CustomEvent('show-user-guide', {});
              window.dispatchEvent(event);
            }}
            aria-label="Ouvrir le guide utilisateur"
            icon={<HelpCircle className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <IconButton
          variant="ghost"
          size="md"
          title="Notifications"
          onClick={() => {
            const event = new CustomEvent('show-help', {
              detail: {
                title: 'Notifications',
                message:
                  'Aucune notification pour le moment.\n\n' +
                  'Les notifications vous informeront des:\n' +
                  "• Mises à jour de l'application\n" +
                  '• Nouvelles fonctionnalités\n' +
                  '• Conseils et astuces',
              },
            });
            window.dispatchEvent(event);
          }}
          aria-label="Notifications"
          icon={<Bell className="w-4 h-4" />}
        />
        <ThemeToggleButton />
        <Button
          variant="primary"
          size="md"
          onClick={() => setShowExportModal(true)}
          leftIcon={<Download className="w-4 h-4" />}
          aria-label="Exporter le projet"
        >
          Finaliser & Exporter
        </Button>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />
      )}

      {/* A03 : modal de résolution de conflit multi-onglets (écoute l'event
          'snapbooth:save-conflict' émis par l'autosave) */}
      <SaveConflictModal />
    </header>
  );
}

/**
 * Outils pour multi-sélection
 * PHASE 3: Utilise updateShapes (batch) pour éviter le spam d'historique
 */
const MultiSelectionTools: React.FC<{ shapes: ShapeData[] }> = ({ shapes }) => {
  const deleteShape = useAppStore(state => state.deleteShape);
  // P0.2 FIX: Utiliser selection.selectedIds au lieu de canvas.selectedNodeIds
  const selectedNodeIds = useAppStore(state => state.selection.selectedIds);
  const { showSuccess } = useToast();

  // AMÉLIORATION: Utiliser AlignmentService pour toutes les opérations
  const shapeIds = selectedNodeIds.length > 0 ? selectedNodeIds : shapes.map(s => s.id);

  const handleAlign = (alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    alignmentService.alignShapes(shapeIds, alignment);
    showSuccess(`${shapes.length} élément(s) aligné(s)`);
  };

  const handleDistribute = (direction: 'horizontal' | 'vertical') => {
    alignmentService.distributeShapes(shapeIds, direction);
    showSuccess(`${shapes.length} élément(s) distribué(s)`);
  };

  const handleCenter = () => {
    alignmentService.centerShapes(shapeIds);
    showSuccess(`${shapes.length} élément(s) centré(s)`);
  };

  const handleDelete = () => {
    shapes.forEach(shape => deleteShape(shape.id));
  };

  return (
    <>
      <div className="topbar-tool-group">
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginRight: 'var(--spacing-sm)',
          }}
        >
          {shapes.length} éléments sélectionnés
        </span>
      </div>

      <div className="tool-separator" />

      {/* AMÉLIORATION: Barre d'outils complète pour multi-sélection */}
      <div className="topbar-tool-group flex items-center gap-1">
        <EnhancedTooltip content="Aligner à gauche">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleAlign('left')}
            aria-label="Aligner à gauche"
            icon={<AlignLeft className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Aligner au centre">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleAlign('center')}
            aria-label="Aligner au centre"
            icon={<AlignCenter className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Aligner à droite">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleAlign('right')}
            aria-label="Aligner à droite"
            icon={<AlignRight className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Aligner en haut">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleAlign('top')}
            aria-label="Aligner en haut"
            icon={<AlignTop className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Aligner au milieu">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleAlign('middle')}
            aria-label="Aligner au milieu"
            icon={<AlignVerticalCenter className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Aligner en bas">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleAlign('bottom')}
            aria-label="Aligner en bas"
            icon={<AlignBottom className="w-4 h-4" />}
          />
        </EnhancedTooltip>
      </div>

      <div className="tool-separator" />

      <div className="topbar-tool-group flex items-center gap-1">
        <EnhancedTooltip content="Distribuer horizontalement">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleDistribute('horizontal')}
            disabled={shapes.length < 3}
            aria-label="Distribuer horizontalement"
            icon={<DistributeHorizontal className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Distribuer verticalement">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => handleDistribute('vertical')}
            disabled={shapes.length < 3}
            aria-label="Distribuer verticalement"
            icon={<DistributeVertical className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Centrer sur le canvas">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={handleCenter}
            aria-label="Centrer sur le canvas"
            icon={<Move className="w-4 h-4" />}
          />
        </EnhancedTooltip>
      </div>

      <div className="tool-separator" />

      <div className="topbar-tool-group flex items-center gap-1">
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          title="Supprimer la sélection"
          aria-label="Supprimer la sélection"
          icon={<Trash2 className="w-4 h-4" />}
        />
      </div>
    </>
  );
};

/**
 * Outils pour sélection unique
 */
const SingleSelectionTools: React.FC<{ shape: ShapeData }> = ({ shape }) => {
  const updateShape = useAppStore(state => state.updateShape);
  const deleteShape = useAppStore(state => state.deleteShape);
  const duplicateShape = useAppStore(state => state.duplicateShape);
  // P0.2 FIX: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
  const selectedNodeId = useAppStore(state => state.selection.primaryId);

  const handleDelete = () => {
    if (selectedNodeId) {
      deleteShape(selectedNodeId);
    }
  };

  const handleDuplicate = () => {
    if (selectedNodeId) {
      duplicateShape(selectedNodeId);
    }
  };

  const handleCenter = () => {
    // Centrer la forme sur le canvas
    const canvasWidth = useAppStore.getState().canvas.templateWidth;
    const canvasHeight = useAppStore.getState().canvas.templateHeight;
    const shapeWidth = shape.width || 100;
    const shapeHeight = shape.height || 100;
    updateShape(shape.id, {
      x: (canvasWidth - shapeWidth) / 2,
      y: (canvasHeight - shapeHeight) / 2,
    });
  };

  // Afficher les outils de texte si c'est un texte
  if (shape.type === 'text') {
    // REMPLACÉ: Log remplacé par logger central (dev only)
    logger.debug(
      'Affichage des outils de texte pour shape',
      { shapeId: shape.id, shape },
      'Topbar'
    );
    return (
      <>
        <TextTools shape={shape} onUpdate={updateShape} />
        <div className="tool-separator" />
        <div className="topbar-tool-group flex items-center gap-1">
          <EnhancedTooltip content="Centrer sur le canvas" shortcut="Ctrl+Shift+M">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={handleCenter}
              aria-label="Centrer sur le canvas"
              icon={<Move className="w-4 h-4" />}
            />
          </EnhancedTooltip>
          <EnhancedTooltip content="Dupliquer" shortcut="Ctrl+D">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={handleDuplicate}
              aria-label="Dupliquer"
              icon={<Copy className="w-4 h-4" />}
            />
          </EnhancedTooltip>
          <EnhancedTooltip content="Supprimer" shortcut="Delete">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              aria-label="Supprimer"
              icon={<Trash2 className="w-4 h-4" />}
            />
          </EnhancedTooltip>
        </div>
      </>
    );
  }

  return (
    <div
      className="topbar-tool-group"
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}
    >
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
        {shape.type.charAt(0).toUpperCase() + shape.type.slice(1)}
      </span>
      <div className="tool-separator" />
      <EnhancedTooltip content="Centrer sur le canvas">
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleCenter}
          aria-label="Centrer sur le canvas"
          icon={<Move className="w-4 h-4" />}
        />
      </EnhancedTooltip>
      <EnhancedTooltip content="Dupliquer" shortcut="Ctrl+D">
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleDuplicate}
          aria-label="Dupliquer"
          icon={<Copy className="w-4 h-4" />}
        />
      </EnhancedTooltip>
      <EnhancedTooltip content="Supprimer" shortcut="Delete">
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          aria-label="Supprimer"
          icon={<Trash2 className="w-4 h-4" />}
        />
      </EnhancedTooltip>
    </div>
  );
};

/**
 * Outils pour texte - Barre d'outils complète
 */
const TextTools: React.FC<{
  shape: ShapeData;
  onUpdate: (id: string, updates: Partial<ShapeData>) => void;
}> = ({ shape, onUpdate }) => {
  const handleUpdate = useCallback(
    (updates: Partial<ShapeData>) => {
      onUpdate(shape.id, updates);
    },
    [shape.id, onUpdate]
  );

  return (
    <>
      {/* Police */}
      <div className="topbar-tool-group">
        <EnhancedTooltip content="Police">
          <select
            value={shape.fontFamily || 'Arial'}
            onChange={e => handleUpdate({ fontFamily: e.target.value })}
            style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-primary)',
              fontSize: 'var(--font-size-sm)',
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              minWidth: '120px',
            }}
          >
            <option value="Arial">Arial</option>
            <option value="Helvetica">Helvetica</option>
            <option value="Inter">Inter</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
          </select>
        </EnhancedTooltip>
      </div>

      <div className="tool-separator" />

      {/* Taille */}
      <div className="topbar-tool-group">
        <EnhancedTooltip content="Taille de police">
          <div className="flex items-center gap-1">
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => handleUpdate({ fontSize: Math.max(8, (shape.fontSize || 24) - 2) })}
              title="Réduire"
              aria-label="Réduire la taille"
              icon={<Minus className="w-3 h-3" />}
            />
            <input
              type="number"
              value={Math.round(shape.fontSize || 24)}
              onChange={e =>
                handleUpdate({
                  fontSize: Math.max(8, Math.min(200, parseFloat(e.target.value) || 24)),
                })
              }
              className="w-[60px] bg-gray-50 border border-gray-300 text-gray-900 text-sm px-2 py-1.5 rounded text-center"
              min={8}
              max={200}
            />
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => handleUpdate({ fontSize: Math.min(200, (shape.fontSize || 24) + 2) })}
              title="Augmenter"
              aria-label="Augmenter la taille"
              icon={<Plus className="w-3 h-3" />}
            />
            <span className="text-xs text-gray-500 ml-1">px</span>
          </div>
        </EnhancedTooltip>
      </div>

      <div className="tool-separator" />

      {/* Couleur */}
      <div className="topbar-tool-group">
        <EnhancedTooltip content="Couleur du texte">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="color"
              value={shape.fill || '#000000'}
              onChange={e => handleUpdate({ fill: e.target.value })}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                padding: 0,
              }}
              title="Couleur du texte"
            />
            <input
              type="text"
              value={(shape.fill || '#000000').toUpperCase()}
              onChange={e => handleUpdate({ fill: e.target.value })}
              style={{
                width: '70px',
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text-primary)',
                fontSize: 'var(--font-size-xs)',
                padding: '6px 8px',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'monospace',
              }}
              placeholder="#000000"
            />
          </div>
        </EnhancedTooltip>
      </div>

      <div className="tool-separator" />

      {/* Styles (Gras, Italique, Souligné) */}
      <div className="topbar-tool-group flex items-center gap-1">
        <EnhancedTooltip content="Gras">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => {
              const currentStyle = shape.fontStyle || '';
              const hasBold = currentStyle.includes('bold') || shape.fontWeight === 'bold';
              handleUpdate({
                fontStyle: hasBold
                  ? currentStyle.replace(/\bbold\b/g, '').trim() || 'normal'
                  : `${currentStyle} bold`.trim(),
                fontWeight: hasBold ? 'normal' : 'bold',
              });
            }}
            aria-label="Gras"
            className={
              shape.fontWeight === 'bold' || shape.fontStyle?.includes('bold')
                ? 'bg-blue-50 text-blue-600'
                : ''
            }
            icon={<Bold className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Italique">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => {
              const currentStyle = shape.fontStyle || '';
              const hasItalic = currentStyle.includes('italic');
              handleUpdate({
                fontStyle: hasItalic
                  ? currentStyle.replace(/\bitalic\b/g, '').trim() || 'normal'
                  : `${currentStyle} italic`.trim(),
              });
            }}
            aria-label="Italique"
            className={shape.fontStyle?.includes('italic') ? 'bg-blue-50 text-blue-600' : ''}
            icon={<Italic className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Souligné">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => {
              handleUpdate({
                textDecoration: shape.textDecoration === 'underline' ? 'none' : 'underline',
              });
            }}
            aria-label="Souligné"
            className={shape.textDecoration === 'underline' ? 'bg-blue-50 text-blue-600' : ''}
            icon={<Underline className="w-4 h-4" />}
          />
        </EnhancedTooltip>
      </div>

      <div className="tool-separator" />

      {/* Alignement */}
      <div className="topbar-tool-group flex items-center gap-1">
        {(['left', 'center', 'right', 'justify'] as const).map(align => (
          <EnhancedTooltip
            key={align}
            content={
              align === 'left'
                ? 'Gauche'
                : align === 'center'
                  ? 'Centré'
                  : align === 'right'
                    ? 'Droite'
                    : 'Justifié'
            }
          >
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => handleUpdate({ textAlign: align })}
              aria-label={
                align === 'left'
                  ? 'Gauche'
                  : align === 'center'
                    ? 'Centré'
                    : align === 'right'
                      ? 'Droite'
                      : 'Justifié'
              }
              className={shape.textAlign === align ? 'bg-blue-50 text-blue-600' : ''}
              icon={
                align === 'left' ? (
                  <AlignLeft className="w-4 h-4" />
                ) : align === 'center' ? (
                  <AlignCenter className="w-4 h-4" />
                ) : align === 'right' ? (
                  <AlignRight className="w-4 h-4" />
                ) : (
                  <AlignJustify className="w-4 h-4" />
                )
              }
            />
          </EnhancedTooltip>
        ))}
      </div>
    </>
  );
};

/**
 * Outils pour vue d'ensemble
 */
const OverviewTools: React.FC = () => {
  const zoomLevel = useAppStore(state => state.canvas.zoomLevel);
  const setZoomLevel = useAppStore(state => state.setZoomLevel);
  const backgroundColor = useAppStore(state => state.canvas.backgroundColor);
  const setBackgroundColor = useAppStore(state => state.setBackgroundColor);
  const showSafeZone = useAppStore(state => state.project.safeZoneVisible);
  const setShowSafeZone = useAppStore(state => state.setSafeZoneVisible);
  const gridEnabled = useAppStore(state => state.canvas.gridEnabled);
  const setGridEnabled = useAppStore(state => state.setGridEnabled);
  const canUndo = useAppStore(state => state.history.canUndo);
  const canRedo = useAppStore(state => state.history.canRedo);
  const undo = useAppStore(state => state.undo);
  const redo = useAppStore(state => state.redo);

  const handleZoomIn = () => {
    setZoomLevel(Math.min(5, zoomLevel * 1.2));
  };

  const handleZoomOut = () => {
    setZoomLevel(Math.max(0.1, zoomLevel / 1.2));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  const _handleFitToScreen = () => {
    // Calculer le zoom pour que tout le canvas soit visible dans la viewport
    const canvasWidth = useAppStore.getState().canvas.templateWidth;
    const canvasHeight = useAppStore.getState().canvas.templateHeight;

    // Dimensions de la viewport (zone visible du canvas)
    // Récupérer depuis le container du canvas si disponible
    const canvasContainer = document.querySelector('.snapbooth-canvas-area');
    const viewportWidth = canvasContainer?.clientWidth || 1200;
    const viewportHeight = canvasContainer?.clientHeight || 800;

    // Calculer le ratio de zoom pour que le canvas rentre dans la viewport avec marge
    const margin = 40; // Marge de 20px de chaque côté
    const scaleX = (viewportWidth - margin) / canvasWidth;
    const scaleY = (viewportHeight - margin) / canvasHeight;
    const newZoom = Math.min(scaleX, scaleY, 5) * 0.9; // 90% pour laisser de la marge, limiter à 5x max

    setZoomLevel(Math.max(0.1, newZoom));
  };

  return (
    <div
      className="topbar-tool-group"
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}
    >
      {/* Zoom Controls */}
      <div className="flex items-center gap-1">
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleZoomOut}
          title="Zoom arrière"
          aria-label="Zoom arrière"
          icon={<ZoomOut className="w-4 h-4" />}
        />
        <span className="text-sm text-gray-900 min-w-[60px] text-center">
          {Math.round(zoomLevel * 100)}%
        </span>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleZoomIn}
          title="Zoom avant"
          aria-label="Zoom avant"
          icon={<ZoomIn className="w-4 h-4" />}
        />
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleZoomReset}
          title="Réinitialiser le zoom"
          aria-label="Réinitialiser le zoom"
          icon={<RotateCcw className="w-4 h-4" />}
        />
      </div>

      <div className="tool-separator" />

      {/* Background Color */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
        <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          Fond:
        </label>
        <input
          type="color"
          value={backgroundColor}
          onChange={e => setBackgroundColor(e.target.value)}
          style={{
            width: '32px',
            height: '24px',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
          title="Couleur de fond"
        />
      </div>

      <div className="tool-separator" />

      {/* Undo/Redo Controls */}
      <div className="flex items-center gap-1">
        <EnhancedTooltip content="Annuler" shortcut="Ctrl+Z">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={undo}
            disabled={!canUndo}
            aria-label="Annuler"
            icon={<Undo2 className="w-4 h-4" />}
          />
        </EnhancedTooltip>
        <EnhancedTooltip content="Rétablir" shortcut="Ctrl+Shift+Z">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={redo}
            disabled={!canRedo}
            aria-label="Rétablir"
            icon={<Redo2 className="w-4 h-4" />}
          />
        </EnhancedTooltip>
      </div>

      <div className="tool-separator" />

      {/* Grid Toggle */}
      <IconButton
        variant="ghost"
        size="sm"
        onClick={() => setGridEnabled(!gridEnabled)}
        title="Afficher/Masquer la grille"
        aria-label="Afficher/Masquer la grille"
        aria-pressed={gridEnabled}
        icon={<Grid3x3 className="w-4 h-4" />}
        className={gridEnabled ? 'bg-blue-50 text-blue-600' : ''}
      />

      <div className="tool-separator" />

      {/* Safe Zone Toggle */}
      <IconButton
        variant="ghost"
        size="sm"
        onClick={() => setShowSafeZone(!showSafeZone)}
        title="Afficher/Masquer la zone de sécurité"
        aria-label="Afficher/Masquer la zone de sécurité"
        aria-pressed={showSafeZone}
        icon={<Square className="w-4 h-4" />}
        className={showSafeZone ? 'bg-blue-50 text-blue-600' : ''}
      />
    </div>
  );
};

/**
 * Bouton de bascule de thème
 */
const ThemeToggleButton: React.FC = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <IconButton
      variant="ghost"
      size="md"
      onClick={toggleTheme}
      title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
      aria-label={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
      aria-pressed={theme === 'dark'}
      role="switch"
      icon={theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    />
  );
};

export default SnapboothTopbar;
